<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { cardActions } from "@/action/cardActions";
import { getBoardIdsOfGroupedStickies } from "@/backend/read/BackendReader";
import { cardKey, cardMetaKey, cardMethodsKey } from "@/components/StickyNote/injectKeys";
import { actionSourceKey } from "@/components/action/injectKeys";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import ListItem from "@/components/ui/ComplexList/ListItem.vue";
import { dropdownKey } from "@/components/ui/DropdownMenu/injectKeys";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListHeader from "@/components/ui/MenuList/MenuListHeader.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import MenuListTitle from "@/components/ui/MenuList/MenuListTitle.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { boardIcon, boardTypeName } from "@/model/baseTypes";
import type { Board } from "@/model/board";
import { stickyIdHref } from "@/router/navigation";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { injectStrict } from "@/utils/vue";

const boardsStickyMirroredTo = ref<string[]>([]);
const stickyIdsByBoard = ref<Record<string, string>>({});

const card = injectStrict(cardKey);
const cardMeta = injectStrict(cardMetaKey);
const dropdown = injectStrict(dropdownKey);
const cardMethods = injectStrict(cardMethodsKey);
const actionSource = injectStrict(actionSourceKey);

const emit = defineEmits<{ mirror: [] }>();

const { t } = useI18n();

onMounted(async () => {
  const result = await getBoardIdsOfGroupedStickies(card.id);
  boardsStickyMirroredTo.value = result.boardIds;
  stickyIdsByBoard.value = Object.fromEntries(
    result.stickies.map((s) => [s.board_id, s.sticky_id]),
  );
});

const boardsStore = useBoardsStore();
const currentBoardId = computed(() => useBoardStore().currentBoard().id);

const boards = computed(() => {
  const mirroredBoardIds = boardsStickyMirroredTo.value;

  // Boards already mirrored to (excluding current board)
  const mirroredBoards = mirroredBoardIds
    .filter((id) => id !== currentBoardId.value)
    .map((id) => boardsStore.boardById(id));

  // Additional target boards not yet mirrored
  const additionalTargets = boardsStore
    .mirrorTargetBoards([card])
    .filter((board) => !mirroredBoardIds.includes(board.id));

  // Current board
  const currentBoard = boardsStore.boardById(currentBoardId.value);

  const allBoards = [...mirroredBoards, ...additionalTargets];
  const byType = boardsStore.boardTypeOrder;
  allBoards.sort((a, b) => byType(a, b) || boardName(a).localeCompare(boardName(b)));

  return [currentBoard, ...allBoards];
});

const boardName = (board: Board): string => {
  return board.type === "team" ? board.team.name : t(boardTypeName(board.type));
};

const isCurrentBoard = (boardId: string) => {
  return boardId === currentBoardId.value;
};

const isAlreadyMirrored = (boardId: string) => {
  return boardsStickyMirroredTo.value.includes(boardId);
};

const mirrorToBoard = async (targetBoard: Board) => {
  if (cardMeta.isReadonly) return;

  boardsStickyMirroredTo.value.push(targetBoard.id);
  await cardActions.mirror(actionSource, card.id, card.teamId, targetBoard);
  emit("mirror");

  dropdown.close();
  void cardMethods.animate?.("mirroring");
};

const mirroredBoardHref = (board: Board) =>
  stickyIdHref(stickyIdsByBoard.value[board.id], board, "enlarge");
</script>

<template>
  <MenuList class="mirror-sticky" role="dialog" aria-labelledby="mirror-sticky-title">
    <MenuListHeader id="mirror-sticky-title">
      <MenuListTitle>{{ $t("cardAction.mirror") }}</MenuListTitle>
    </MenuListHeader>
    <MenuListItems static :aria-label="$t('cardAction.mirror')" role="list" class="items">
      <ListItem
        v-for="targetBoard in boards"
        :key="targetBoard.id"
        compact
        :data-testid="`mirror-sticky-list-item-${targetBoard.id}`"
        class="item"
      >
        <template #before>
          <SvgIcon :name="boardIcon(targetBoard.type)" width="20" height="20" class="board-icon" />
        </template>
        <div class="name">
          <span class="board-name">{{ boardName(targetBoard) }}</span>
          <IconButton
            v-if="!isCurrentBoard(targetBoard.id) && isAlreadyMirrored(targetBoard.id)"
            icon="general/external-link"
            size="x-small"
            flat
            :href="mirroredBoardHref(targetBoard)"
            :text="$t('mirrorSticky.openBoard')"
            data-testid="open-mirrored-board-link"
          />
        </div>
        <template #after>
          <!-- No button for the current board -->
          <BaseButton
            v-if="!isCurrentBoard(targetBoard.id)"
            variant="outlined"
            color="grey"
            size="small"
            :disabled="isAlreadyMirrored(targetBoard.id) || cardMeta.isReadonly"
            :data-testid="
              isAlreadyMirrored(targetBoard.id) ? 'mirrored-placeholder' : 'mirror-button'
            "
            @click="mirrorToBoard(targetBoard)"
          >
            {{
              isAlreadyMirrored(targetBoard.id)
                ? $t("actionMenu.mirrored")
                : $t("cardAction.mirror")
            }}
          </BaseButton>
        </template>
      </ListItem>
    </MenuListItems>
  </MenuList>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";
@use "@/styles/mixins/utils";

.mirror-sticky {
  .items {
    padding: 0;

    :deep(.item) {
      height: 42px;
      padding: 8px 12px;

      .before {
        align-self: unset;
        color: colors.$icon-subtle;
      }

      .content .title {
        height: auto;
        overflow: visible;
      }

      // Compact mirror / "Mirrored" button
      .after .base-button {
        @include typography.small;

        height: 24px;
        padding-inline: 6px;
      }
    }
  }

  .name {
    display: flex;
    align-items: center;
    gap: 4px;

    .board-name {
      @include utils.ellipsis;

      min-width: 0;
    }

    .base-tooltip {
      display: flex;
      align-items: center;
    }
  }
}
</style>
